import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// Import all locale files
import en from '../locales/en.json';
import tr from '../locales/tr.json';
import es from '../locales/es.json';
import pt from '../locales/pt.json';
import hi from '../locales/hi.json';
import ko from '../locales/ko.json';
import zh from '../locales/zh.json';
import el from '../locales/el.json';
import ar from '../locales/ar.json';
import it from '../locales/it.json';
import gu from '../locales/gu.json';

const LanguageContext = createContext();

// All translations mapped by language code
const translations = { en, tr, es, pt, hi, ko, zh, el, ar, it, gu };

// Supported languages in specified order
export const LANGUAGES = {
  en: { code: 'en', name: 'English', flag: '🇺🇸', native: 'English' },
  es: { code: 'es', name: 'Spanish', flag: '🇪🇸', native: 'Español' },
  tr: { code: 'tr', name: 'Turkish', flag: '🇹🇷', native: 'Türkçe' },
  pt: { code: 'pt', name: 'Portuguese', flag: '🇧🇷', native: 'Português' },
  hi: { code: 'hi', name: 'Hindi', flag: '🇮🇳', native: 'हिन्दी' },
  ko: { code: 'ko', name: 'Korean', flag: '🇰🇷', native: '한국어' },
  zh: { code: 'zh', name: 'Chinese', flag: '🇨🇳', native: '中文' },
  el: { code: 'el', name: 'Greek', flag: '🇬🇷', native: 'Ελληνικά' },
  ar: { code: 'ar', name: 'Arabic', flag: '🇸🇦', native: 'العربية', rtl: true },
  it: { code: 'it', name: 'Italian', flag: '🇮🇹', native: 'Italiano' },
  gu: { code: 'gu', name: 'Gujarati', flag: '🇮🇳', native: 'ગુજરાતી' },
};

// Order of languages for display
export const LANGUAGE_ORDER = ['en', 'es', 'tr', 'pt', 'hi', 'ko', 'zh', 'el', 'ar', 'it', 'gu'];

// Primary toggle languages
export const PRIMARY_LANGUAGES = ['en', 'tr'];

// All languages for dropdown (in order)
export const DROPDOWN_LANGUAGES = LANGUAGE_ORDER;

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper function to get nested value from object using dot notation
const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// DEPRECATED: Old translation helper - kept for backward compatibility during migration
// Components should migrate to using t() function from useLanguage() hook
export const getTranslation = (content, language) => {
  if (content[language]) return content[language];
  if (content['en']) return content['en'];
  if (content['tr']) return content['tr'];
  return content[Object.keys(content)[0]];
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('pirone-language');
      if (saved && LANGUAGES[saved]) return saved;
    }
    return 'en';
  });

  const updateLanguage = useCallback((newLang) => {
    setLanguage(newLang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('pirone-language', newLang);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'en' ? 'tr' : 'en';
    updateLanguage(newLang);
  }, [language, updateLanguage]);

  const setGlobalLanguage = useCallback((langCode) => {
    if (LANGUAGES[langCode]) {
      updateLanguage(langCode);
    }
  }, [updateLanguage]);

  // Main translation function - gets value from JSON files
  // Usage: t('home.badge') or t('common.services')
  const t = useCallback((key, fallback = '') => {
    // Try current language first
    const currentTranslations = translations[language];
    let value = getNestedValue(currentTranslations, key);
    
    if (value !== undefined) return value;
    
    // Fallback to English
    value = getNestedValue(translations.en, key);
    if (value !== undefined) return value;
    
    // Fallback to Turkish
    value = getNestedValue(translations.tr, key);
    if (value !== undefined) return value;
    
    // Return fallback or key itself
    return fallback || key;
  }, [language]);

  const currentLanguage = LANGUAGES[language] || LANGUAGES.en;
  const isRTL = currentLanguage.rtl || false;

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    language,
    t,
    toggleLanguage,
    setGlobalLanguage,
    currentLanguage,
    isRTL,
    languages: LANGUAGES,
    primaryLanguages: PRIMARY_LANGUAGES,
    dropdownLanguages: DROPDOWN_LANGUAGES,
    languageOrder: LANGUAGE_ORDER
  }), [language, t, toggleLanguage, setGlobalLanguage, currentLanguage, isRTL]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};
